import React, { useState } from 'react';

import { fmtDateOnly } from '../lib/formato';

function PendingReferralsView({ referrals, company, partners, isAdmin, onApprove, onReject }) {
  const [filter, setFilter] = React.useState('pending'); // 'pending' | 'approved' | 'rejected' | 'all'
  const [approvingId, setApprovingId] = React.useState(null);
  const [rejectingId, setRejectingId] = React.useState(null);
  const [copied, setCopied] = React.useState(false);

  const referralLink = `${window.location.origin}${window.location.pathname}?indicar=${company}`;

  const copyLink = () => {
    navigator.clipboard.writeText(referralLink).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  const filtered = referrals.filter(r =>
    filter === 'all' ? true : r.status === filter
  );

  const qualLabels = ['', '❄️ Muito frio', '🌥 Frio', '🌤 Morno', '🔥 Quente', '🚀 Muito quente'];
  const qualColors = ['', 'bg-blue-100 text-blue-700', 'bg-blue-50 text-blue-500', 'bg-yellow-50 text-yellow-700', 'bg-orange-100 text-orange-700', 'bg-red-100 text-red-700'];

  const counts = {
    pending:  referrals.filter(r=>r.status==='pending').length,
    approved: referrals.filter(r=>r.status==='approved').length,
    rejected: referrals.filter(r=>r.status==='rejected').length,
  };

  return <div className="space-y-4 animate-fade-in-up">
    {/* Header */}
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
      <div>
        <h2 className="text-lg font-bold text-gray-900">📥 Indicações de Parceiros</h2>
        <p className="text-sm text-gray-400">Leads enviados via formulário público pelos seus consultores</p>
      </div>
      <button
        onClick={copyLink}
        className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${copied ? 'bg-green-500 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'} shadow-sm`}
      >
        {copied ? '✓ Link copiado!' : '🔗 Copiar link do formulário'}
      </button>
    </div>

    {/* Link preview */}
    <div className="acard rounded-xl px-4 py-3 flex items-center gap-3">
      <span className="text-xs text-gray-400 flex-shrink-0">Link de indicação:</span>
      <code className="text-xs text-blue-600 truncate flex-1">{referralLink}</code>
      <button onClick={copyLink} className="text-xs text-gray-400 hover:text-blue-600 flex-shrink-0 transition-colors">Copiar</button>
    </div>

    {/* Filtros */}
    <div className="flex gap-2 flex-wrap">
      {[
        { k:'pending',  label:`Pendentes (${counts.pending})`  },
        { k:'approved', label:`Aprovados (${counts.approved})` },
        { k:'rejected', label:`Rejeitados (${counts.rejected})`},
        { k:'all',      label:'Todos'                          },
      ].map(f => (
        <button key={f.k} onClick={()=>setFilter(f.k)}
          className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition-all ${
            filter===f.k ? 'bg-blue-600 text-white shadow-sm' : 'bg-white text-gray-500 hover:bg-gray-100 border border-gray-200'
          }`}>
          {f.label}
        </button>
      ))}
    </div>

    {/* Lista */}
    {filtered.length === 0
      ? <div className="acard rounded-2xl p-10 text-center text-gray-400">
          {filter === 'pending' ? 'Nenhuma indicação pendente. 🎉' : 'Nenhuma indicação encontrada.'}
        </div>
      : <div className="space-y-3">
          {filtered.map(ref => (
            <div key={ref.id} className="acard rounded-2xl p-5 hover:shadow-sm transition-shadow">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  {/* Empresa + consultor */}
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <h3 className="t-subhead font-bold text-gray-900">{ref.company_name}</h3>
                    {ref.qualification && (
                      <span className={`t-caption-2 font-semibold px-2 py-0.5 rounded-full ${qualColors[ref.qualification]||''}`}>
                        {qualLabels[ref.qualification]}
                      </span>
                    )}
                    {ref.status === 'approved' && <span className="t-caption-2 font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700">✓ Aprovado</span>}
                    {ref.status === 'rejected' && <span className="t-caption-2 font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-600">✕ Rejeitado</span>}
                    {ref.status === 'pending'  && <span className="t-caption-2 font-semibold px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">⏳ Pendente</span>}
                  </div>
                  <p className="text-xs text-gray-500 mb-2">
                    Indicado por <span className="font-semibold text-gray-700">{ref.consultant_name}</span>
                    {ref.created_at && <> · {fmtDateOnly(ref.created_at.slice(0,10))}</>}
                  </p>
                  {/* Dados de contato */}
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 mb-2">
                    {ref.contact_name  && <span>👤 {ref.contact_name}</span>}
                    {ref.contact_phone && <span>📱 {ref.contact_phone}</span>}
                    {ref.contact_email && <span>📧 {ref.contact_email}</span>}
                    {ref.website       && <span>🌐 {ref.website}</span>}
                  </div>
                  {/* Resumo */}
                  {ref.summary && (
                    <p className="text-xs text-gray-500 bg-gray-50 rounded-xl px-3 py-2 mt-2 italic">
                      "{ref.summary}"
                    </p>
                  )}
                </div>
                {/* Ações (só para admin e pendentes) */}
                {isAdmin && ref.status === 'pending' && (
                  <div className="flex gap-2 flex-shrink-0">
                    <button
                      disabled={approvingId===ref.id}
                      onClick={async () => { setApprovingId(ref.id); await onApprove(ref); setApprovingId(null); }}
                      className="px-3.5 py-1.5 rounded-xl bg-green-500 hover:bg-green-600 text-white text-xs font-semibold transition-colors disabled:opacity-50"
                    >
                      {approvingId===ref.id ? '...' : '✓ Aprovar'}
                    </button>
                    <button
                      disabled={rejectingId===ref.id}
                      onClick={async () => { setRejectingId(ref.id); await onReject(ref.id); setRejectingId(null); }}
                      className="px-3.5 py-1.5 rounded-xl bg-red-100 hover:bg-red-200 text-red-700 text-xs font-semibold transition-colors disabled:opacity-50"
                    >
                      {rejectingId===ref.id ? '...' : '✕ Rejeitar'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>}
  </div>;
}

// ─── App (wrapper de autenticação) ───────────────────────
// ─── PartnerPortalView ────────────────────────────────────

export { PendingReferralsView };
